import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { convertProductValues } from '../../../../components/BoughtChart/convertToChartData';
import { getDataSeriesConfig } from '../../../../components/BoughtChart/boughtChartUtil';
import { GRAPH_VOLUME_COLOR, WHITE } from '../../../../constants/colors';
import capitalize from '@material-ui/core/utils/capitalize';
import { getMonthName } from '../../../../utils/date';
import { CONTRACTED_VOLUME, QUARTERLY_VOLUME_BOUGHT } from '../../../../components/BoughtChart/contants';
import CanvasJSChart from '../../../../assets/CanvasJSChart';
import Grid from '@material-ui/core/Grid';
import styled, { withTheme } from 'styled-components';
import { boughtChartStyle } from '../../../../components/BoughtChart/styles';
import Big from 'big-js';
import { parseNumber } from '../../../../utils/number';
import LegendItem from '../../../../components/Chart/components/LegendItem';

const LegendItemStyle = styled(LegendItem)`
  ${(props) => props.graphTheme.baseFontStyle}
`;

const emptyData = [
  {
    month: 1,
    price: 0,
  },
  {
    month: 2,
    price: 0,
  },
  {
    month: 3,
    price: 0,
  },
  {
    month: 4,
    price: 0,
  },
  {
    month: 5,
    price: 0,
  },
  {
    month: 6,
    price: 0,
  },
  {
    month: 7,
    price: 0,
  },
  {
    month: 8,
    price: 0,
  },
  {
    month: 9,
    price: 0,
  },
  {
    month: 10,
    price: 0,
  },
  {
    month: 11,
    price: 0,
  },
  {
    month: 12,
    price: 0,
  },
];

const PurchaseSimulationComponent = ({ className, data, theme, year, config }) => {
  const intl = useIntl();
  const seriesConfig = getDataSeriesConfig(intl);
  const configNotNull = config || {};

  const prices = data?.prices.map((price) => {
    const output = { ...price };
    if (data?.buying?.find((line) => line.month === price.month)) {
      output.color = seriesConfig[QUARTERLY_VOLUME_BOUGHT].color;
    }
    return output;
  });

  const simulation = data?.buying?.map((line) => {
    const output = { ...line };
    const boughtMonth = data?.prices?.find((total) => line.month === total.month);

    const monthQuantity = new Big(line.quantity).plus(boughtMonth.quantity || 0);
    const monthTotalPrice = new Big(line.quantity)
      .times(data.manualPrice || data.currentPrice || 0)
      .plus(new Big(boughtMonth.price || 0).times(boughtMonth.quantity || 0));
    output.price = parseNumber(monthTotalPrice.div(monthQuantity).toFixed(3));
    return output;
  });

  const nonNullData = prices ? prices : emptyData;
  const chartData = convertProductValues(nonNullData, 'price', year);
  const simulationData = simulation && convertProductValues(simulation, 'price', year);
  const graphTheme = theme.graph;

  const graphData = [
    {
      type: 'column',
      indexLabelFormatter: (e) => intl.formatNumber(e.dataPoint.y),
      indexLabel: '{y}',
      indexLabelFontFamily: graphTheme.baseFontFamily,
      indexLabelFontSize: graphTheme.indexLabelSize,
      indexLabelFontColor: graphTheme.baseFontColor,
      indexLabelPlacement: 'outside',
      indexLabelOrientation: 'horizontal',
      color: seriesConfig[CONTRACTED_VOLUME].color,
      name: seriesConfig[CONTRACTED_VOLUME].name,
      dataPoints: chartData,
    },
  ];

  if (simulationData) {
    graphData.push({
      type: 'column',
      indexLabelFormatter: (e) => intl.formatNumber(e.dataPoint.y),
      indexLabel: '{y}',
      indexLabelFontFamily: graphTheme.baseFontFamily,
      indexLabelFontSize: graphTheme.indexLabelSize,
      indexLabelFontColor: graphTheme.baseFontColor,
      indexLabelPlacement: 'outside',
      indexLabelOrientation: 'horizontal',
      color: seriesConfig['BUYING'].color,
      name: seriesConfig[CONTRACTED_VOLUME].name,
      dataPoints: simulationData,
    });
  }

  const options = {
    animationEnabled: true,
    dataPointWidth: 40,
    axisY: {
      title:
        !configNotNull.hideYAxesName &&
        intl.formatMessage(
          {
            id: `app.contract.buy.purchaseSimulation.axisY.label`,
            defaultMessage: '',
          },
          { currency: data?.currency?.id },
        ),
      lineThickness: 0,
      tickLength: 1,
      gridThickness: 1,
      gridColor: GRAPH_VOLUME_COLOR,
      margin: 20,
      labelFormatter: (e) => (!configNotNull.hideYAxesScale ? intl.formatNumber(e.value) : ''),
      labelFontFamily: graphTheme.baseFontFamily,
      labelFontSize: graphTheme.baseFontSize,
      labelFontColor: graphTheme.baseFontColor,
      titleFontFamily: graphTheme.baseFontFamily,
      titleFontSize: graphTheme.baseFontSize,
      titleFontColor: graphTheme.baseFontColor,
    },
    axisX: {
      tickLength: 15,
      tickColor: WHITE,
      interval: 1,
      intervalType: 'month',
      lineColor: GRAPH_VOLUME_COLOR,
      labelFontFamily: graphTheme.baseFontFamily,
      labelFontSize: graphTheme.baseFontSize,
      labelFontColor: graphTheme.baseFontColor,
      labelFormatter: (e) => {
        return capitalize(getMonthName(intl, e.value));
      },
    },
    toolTip: {
      enabled: false,
    },
    data: graphData,
  };

  const activeAndGraphTheme = {
    active: true,
    graphTheme: graphTheme,
  };

  return (
    <div className={className}>
      <CanvasJSChart options={options} />
      <Grid
        container
        direction={'row'}
        justify={'space-evenly'}
        alignItems={'center'}
        style={{
          marginTop: 5,
        }}>
        <LegendItemStyle
          {...activeAndGraphTheme}
          color={seriesConfig[QUARTERLY_VOLUME_BOUGHT].color}
          label={intl.formatMessage({ id: 'app.purchaseSimulation.averagePriceBefore.label' })}
        />
        <LegendItemStyle
          {...activeAndGraphTheme}
          color={seriesConfig['BUYING'].color}
          label={intl.formatMessage({ id: 'app.purchaseSimulation.averagePriceAfter.label' })}
        />
      </Grid>
    </div>
  );
};

PurchaseSimulationComponent.propTypes = {
  className: PropTypes.string,
  year: PropTypes.number.isRequired,
  data: PropTypes.object,
  theme: PropTypes.shape({
    graph: PropTypes.shape({
      baseFontFamily: PropTypes.string,
      baseFontSize: PropTypes.number,
      baseFontColor: PropTypes.string,
      indexLabelSize: PropTypes.number,
    }),
  }),
  config: PropTypes.shape({
    hideYAxesScale: PropTypes.bool,
    hideYAxesName: PropTypes.bool,
  }),
};

export const PurchaseSimulation = withTheme(boughtChartStyle(PurchaseSimulationComponent));
